import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, Legend, PieChart, Pie, RadialBarChart, RadialBar,
} from 'recharts';
import { host } from '../script/variables';
import '../style/growth.css';

/* ─────────────────────────────────────────────
   UTILITIES
───────────────────────────────────────────── */
const fmt     = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtK    = (n) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(Math.round(n));
const fmtDate = (d) => d.toISOString().slice(0, 10);
const pct     = (curr, prev) => (prev == null || prev === 0) ? null : ((curr - prev) / prev) * 100;
const sign    = (n) => n > 0 ? '+' : '';
const clr     = (n) => n > 0 ? 'pos' : n < 0 ? 'neg' : 'flat';
const iso     = (d) => fmtDate(d);
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

const COLORS = ['#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6','#ec4899','#14b8a6','#f97316'];

/* ─────────────────────────────────────────────
   PERIOD HELPERS
───────────────────────────────────────────── */
const periods = {
  week: () => {
    const end    = new Date(); end.setHours(0,0,0,0);
    const start  = addDays(end, -6);
    const pEnd   = addDays(start, -1);
    const pStart = addDays(pEnd, -6);
    return { label:'Week', curr:{from:iso(start),to:iso(end)}, prev:{from:iso(pStart),to:iso(pEnd)}, days:7 };
  },
  month: () => {
    const now = new Date();
    const cS  = new Date(now.getFullYear(), now.getMonth(), 1);
    const cE  = new Date(now.getFullYear(), now.getMonth()+1, 0);
    const pS  = new Date(now.getFullYear(), now.getMonth()-1, 1);
    const pE  = new Date(now.getFullYear(), now.getMonth(), 0);
    return { label:'Month', curr:{from:iso(cS),to:iso(cE)}, prev:{from:iso(pS),to:iso(pE)}, days:30 };
  },
  year: () => {
    const y = new Date().getFullYear();
    return { label:'Year', curr:{from:`${y}-01-01`,to:`${y}-12-31`}, prev:{from:`${y-1}-01-01`,to:`${y-1}-12-31`}, days:365 };
  },
};

/* ─────────────────────────────────────────────
   API HELPERS
───────────────────────────────────────────── */
const fetchStats = async (cafeid, from, to) => {
  const token = localStorage.getItem('partnertoken');
  const today = iso(new Date());
  const res   = await fetch(`${host}/res_stats/get_revenue_stats`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({ restaurantId: cafeid, selectedDate: today, fromDate: from, toDate: to }),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.message || 'Fetch failed');
  return json;
};

const fetchCustomerAnalytics = async (cafeid, currPeriod, prevPeriod) => {
  const token = localStorage.getItem('partnertoken');
  const res   = await fetch(`${host}/partner/get_customer_analytics`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({
      restaurantId: cafeid,
      fromDate:     currPeriod.from,
      toDate:       currPeriod.to,
      prevFromDate: prevPeriod.from,
      prevToDate:   prevPeriod.to,
    }),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.message || 'Customer fetch failed');
  return json;
};

/* ─────────────────────────────────────────────
   DELTA CARD
───────────────────────────────────────────── */
const DeltaCard = ({ label, curr, prev, prefix='', suffix='', format='number', delay=0, icon }) => {
  const growth  = pct(curr, prev);
  const diff    = curr - prev;
  const c       = growth === null ? 'flat' : clr(growth);
  const [vis, setVis] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVis(true), delay); return () => clearTimeout(t); }, [delay]);

  const display = format === 'currency'
    ? `${prefix}${fmt(Math.round(curr))}${suffix}`
    : `${prefix}${fmt(curr)}${suffix}`;

  return (
    <div className={`delta-card ${vis ? 'dc-visible' : ''}`} style={{ animationDelay:`${delay}ms` }}>
      {icon && <span className="dc-icon">{icon}</span>}
      <p className="dc-label">{label}</p>
      <div className="dc-value-row">
        <h3 className="dc-value">{display}</h3>
        {growth !== null && (
          <span className={`dc-badge dc-${c}`}>
            <span className="dc-arrow">{c==='pos' ? '↑' : c==='neg' ? '↓' : '→'}</span>
            {sign(growth)}{Math.abs(growth).toFixed(1)}%
          </span>
        )}
      </div>
      <p className="dc-prev">
        {growth !== null
          ? `vs ${fmt(Math.round(prev))} last period · ${sign(diff)}${fmt(Math.abs(Math.round(diff)))}`
          : 'No prior period data'}
      </p>
      <div className="dc-bar-track">
        <div className={`dc-bar-fill dc-bar-${c}`}
          style={{ width:`${Math.min(100, prev===0 ? 100 : (curr/Math.max(curr,prev))*100)}%` }} />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   DISH GROWTH ROW
───────────────────────────────────────────── */
const DishGrowthRow = ({ curr, prev, rank }) => {
  const prevItem  = prev.find(p => p.name === curr.name) || { qty:0, revenue:0 };
  const revGrowth = pct(curr.revenue, prevItem.revenue);
  const qtyGrowth = pct(curr.qty,     prevItem.qty);
  const c         = revGrowth === null ? 'flat' : clr(revGrowth);
  return (
    <div className={`dish-row ${c==='pos'?'dr-pos':c==='neg'?'dr-neg':''}`}>
      <div className="dr-rank">{rank}</div>
      <div className="dr-name-col">
        <span className="dr-name">{curr.name}</span>
        <div className="dr-bar-track">
          <div className="dr-bar-fill" style={{
            width: `${Math.min(100, prevItem.revenue===0?100:(curr.revenue/Math.max(curr.revenue,prevItem.revenue))*100)}%`,
            background: COLORS[rank % COLORS.length],
          }}/>
        </div>
      </div>
      <div className="dr-stats">
        <span className="dr-rev">₹{fmt(curr.revenue)}</span>
        {revGrowth !== null
          ? <span className={`dr-delta dr-${c}`}>{c==='pos'?'↑':c==='neg'?'↓':'→'}{Math.abs(revGrowth).toFixed(0)}%</span>
          : <span className="dr-delta dr-flat">New</span>}
      </div>
      <div className="dr-qty">
        <span>{curr.qty} sold</span>
        {qtyGrowth !== null && <span className={`dr-qty-delta ${clr(qtyGrowth)}`}>({sign(qtyGrowth)}{qtyGrowth.toFixed(0)}%)</span>}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   COMPARISON CHART
───────────────────────────────────────────── */
const ComparisonChart = ({ currData, prevData, dataKey, label, color, fmt:fmtFn }) => {
  const merged = currData.map((d, i) => ({
    label:    d.date || d.time || `P${i+1}`,
    current:  d[dataKey] || 0,
    previous: prevData[i]?.[dataKey] || 0,
  }));

  const CustomTooltip = ({ active, payload, label:lbl }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="chart-tooltip">
        <p className="ct-label">{lbl}</p>
        {payload.map(p => (
          <p key={p.name} className="ct-row" style={{ color:p.color }}>
            <span>{p.name === 'current' ? 'This Period' : 'Last Period'}</span>
            <span>{fmtFn ? fmtFn(p.value) : fmt(p.value)}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={merged} margin={{ top:8, right:16, left:0, bottom:0 }}>
        <defs>
          <linearGradient id={`curr-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={color}     stopOpacity={0.18}/>
            <stop offset="95%" stopColor={color}     stopOpacity={0}/>
          </linearGradient>
          <linearGradient id={`prev-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#94a3b8" stopOpacity={0.12}/>
            <stop offset="95%" stopColor="#94a3b8" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" vertical={false}/>
        <XAxis dataKey="label" tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false}/>
        <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
        <Tooltip content={<CustomTooltip/>}/>
        <Area type="monotone" dataKey="previous" stroke="#94a3b8" strokeWidth={1.5}
          strokeDasharray="4 2" fill={`url(#prev-${dataKey})`} name="previous" dot={false}/>
        <Area type="monotone" dataKey="current" stroke={color} strokeWidth={2.5}
          fill={`url(#curr-${dataKey})`} name="current" dot={false} activeDot={{ r:5, strokeWidth:0 }}/>
      </AreaChart>
    </ResponsiveContainer>
  );
};

/* ─────────────────────────────────────────────
   MONTHLY BAR (year view)
───────────────────────────────────────────── */
const MonthlyBar = ({ data, prevData }) => {
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const byMonth = Array(12).fill(null).map((_, i) => ({ name:MONTHS[i], curr:0, prev:0 }));
  (data    || []).forEach(d => { const m = new Date(d.date).getMonth(); if (!isNaN(m)) byMonth[m].curr += d.revenue || 0; });
  (prevData|| []).forEach(d => { const m = new Date(d.date).getMonth(); if (!isNaN(m)) byMonth[m].prev += d.revenue || 0; });

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const curr = payload.find(p => p.dataKey === 'curr')?.value || 0;
    const prev = payload.find(p => p.dataKey === 'prev')?.value || 0;
    const g    = pct(curr, prev);
    return (
      <div className="chart-tooltip">
        <p className="ct-label">{label}</p>
        <p className="ct-row"><span>This Year</span><span>₹{fmt(Math.round(curr))}</span></p>
        <p className="ct-row"><span>Last Year</span><span>₹{fmt(Math.round(prev))}</span></p>
        {g !== null && <p className={`ct-growth ${clr(g)}`}>{sign(g)}{g.toFixed(1)}% YoY</p>}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={byMonth} barGap={2} margin={{ top:8, right:8, left:0, bottom:0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" vertical={false}/>
        <XAxis dataKey="name" tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false}/>
        <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
        <Tooltip content={<CustomTooltip/>}/>
        <Bar dataKey="prev" fill="#e2e8f0" radius={[3,3,0,0]} name="Last Year"/>
        <Bar dataKey="curr" radius={[3,3,0,0]} name="This Year">
          {byMonth.map((d, i) => (
            <Cell key={i} fill={d.curr > d.prev ? '#6366f1' : d.curr < d.prev ? '#f59e0b' : '#cbd5e1'}/>
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/* ─────────────────────────────────────────────
   GROWTH BANNER (sales)
───────────────────────────────────────────── */
const GrowthBanner = ({ curr, prev, period }) => {
  const rev = pct(curr?.totalRevenue,  prev?.totalRevenue);
  const ord = pct(curr?.totalOrders,   prev?.totalOrders);
  const aov = pct(curr?.avgOrderValue, prev?.avgOrderValue);
  const Icon = ({ v }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
      {v > 0 ? <path d="M5 17L12 7l7 10"/> : v < 0 ? <path d="M5 7l7 10 7-10"/> : <path d="M5 12h14"/>}
    </svg>
  );
  return (
    <div className="growth-banner">
      <div className="gb-title">
        <span className="gb-period">{period}-over-{period} Growth</span>
        <span className="gb-sub">Compared to the same {period.toLowerCase()} last cycle</span>
      </div>
      <div className="gb-metrics">
        {[
          { label:'Revenue',   val:rev },
          { label:'Orders',    val:ord },
          { label:'Avg Order', val:aov },
        ].map(m => (
          <div key={m.label} className={`gb-metric gb-${m.val===null?'flat':clr(m.val)}`}>
            <Icon v={m.val}/>
            <div>
              <p className="gb-metric-val">{m.val===null ? 'N/A' : `${sign(m.val)}${Math.abs(m.val).toFixed(1)}%`}</p>
              <p className="gb-metric-label">{m.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   RETENTION GAUGE
───────────────────────────────────────────── */
const RetentionGauge = ({ value }) => {
  const band =
    value >= 40 ? { label:'Excellent', color:'#10b981' } :
    value >= 20 ? { label:'Decent',    color:'#f59e0b' } :
                  { label:'Weak',      color:'#ef4444' };

  const circumference = 2 * Math.PI * 54;
  const filled        = (value / 100) * circumference;

  return (
    <div className="retention-gauge">
      <svg viewBox="0 0 120 120" width="120" height="120">
        <circle cx="60" cy="60" r="54" fill="none" stroke="#f1f5f9" strokeWidth="12"/>
        <circle cx="60" cy="60" r="54" fill="none"
          stroke={band.color} strokeWidth="12"
          strokeDasharray={`${filled} ${circumference}`}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
          style={{ transition:'stroke-dasharray .8s ease' }}
        />
        <text x="60" y="56" textAnchor="middle" fontSize="20" fontWeight="700" fill="#1e293b">{value.toFixed(0)}%</text>
        <text x="60" y="72" textAnchor="middle" fontSize="9"  fill="#94a3b8">Retention</text>
      </svg>
      <span className="rg-label" style={{ color:band.color }}>{band.label}</span>
    </div>
  );
};

/* ─────────────────────────────────────────────
   METRIC INSIGHT CARD
───────────────────────────────────────────── */
const InsightCard = ({ icon, title, value, subtitle, color, delay=0 }) => {
  const [vis, setVis] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVis(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div className={`insight-card ${vis?'ic-visible':''}`} style={{ animationDelay:`${delay}ms`, '--ic-color':color }}>
      <div className="ic-icon-wrap"><span className="ic-icon">{icon}</span></div>
      <div className="ic-body">
        <p className="ic-title">{title}</p>
        <p className="ic-value" style={{ color }}>{value}</p>
        <p className="ic-sub">{subtitle}</p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   AGGREGATED TREND CHART
   Replaces daily stacked bar — works at any scale.
   trendData: [{ period, visits, newCustomers,
                 repeatCustomers, revenue,
                 revGrowthPct, visitGrowthPct }]
   aggregation: "daily" | "weekly" | "monthly"
───────────────────────────────────────────── */
const TrendChart = ({ data = [], aggregation = 'monthly' }) => {
  const [metric, setMetric] = useState('revenue'); // 'revenue' | 'visits' | 'mix'

  const labelMap = {
    daily:   'Day',
    weekly:  'Week',
    monthly: 'Month',
  };

  // Tick labels — shorten for readability
  const formatTick = (val) => {
    if (!val) return '';
    // Monthly "2024-03" → "Mar'24"
    if (/^\d{4}-\d{2}$/.test(val)) {
      const [y, m] = val.split('-');
      const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(m)-1];
      return `${mon}'${y.slice(2)}`;
    }
    // Weekly "2024-W03" → "W03"
    if (/W\d{2}/.test(val)) return val.split('-')[1];
    // Daily "2024-03-15" → "15 Mar"
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const d = new Date(val);
      return `${d.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]}`;
    }
    return val;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = data.find(r => r.period === label) || {};
    return (
      <div className="chart-tooltip">
        <p className="ct-label">{formatTick(label)}</p>
        <p className="ct-row"><span>Revenue</span><span>₹{fmt(Math.round(d.revenue || 0))}</span></p>
        <p className="ct-row"><span>Total Visits</span><span>{d.visits || 0}</span></p>
        <p className="ct-row" style={{ color:'#6366f1' }}><span>New Customers</span><span>{d.newCustomers || 0}</span></p>
        <p className="ct-row" style={{ color:'#10b981' }}><span>Repeat Customers</span><span>{d.repeatCustomers || 0}</span></p>
        {d.revGrowthPct !== null && d.revGrowthPct !== undefined && (
          <p className={`ct-growth ${clr(d.revGrowthPct)}`}>
            Rev {sign(d.revGrowthPct)}{d.revGrowthPct}% vs prev {labelMap[aggregation]}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="trend-chart-wrap">
      {/* Metric selector pills */}
      <div className="trend-pills">
        {[
          { key:'revenue', label:'Revenue'       },
          { key:'visits',  label:'Visit Volume'  },
          { key:'mix',     label:'New vs Repeat' },
        ].map(opt => (
          <button
            key={opt.key}
            className={`trend-pill ${metric === opt.key ? 'trend-pill-on' : ''}`}
            onClick={() => setMetric(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div style={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          {metric === 'mix' ? (
            // Stacked bar: new vs repeat customers per period
            <BarChart data={data} margin={{ top:8, right:8, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" vertical={false}/>
              <XAxis dataKey="period" tickFormatter={formatTick} tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:11 }}/>
              <Bar dataKey="newCustomers"    stackId="a" fill="#6366f1" radius={[0,0,0,0]} name="New"/>
              <Bar dataKey="repeatCustomers" stackId="a" fill="#10b981" radius={[3,3,0,0]} name="Repeat"/>
            </BarChart>
          ) : (
            // Area chart: revenue or visits
            <AreaChart data={data} margin={{ top:8, right:16, left:0, bottom:0 }}>
              <defs>
                <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={metric==='revenue'?'#6366f1':'#10b981'} stopOpacity={0.18}/>
                  <stop offset="95%" stopColor={metric==='revenue'?'#6366f1':'#10b981'} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" vertical={false}/>
              <XAxis dataKey="period" tickFormatter={formatTick} tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Area
                type="monotone"
                dataKey={metric === 'revenue' ? 'revenue' : 'visits'}
                stroke={metric === 'revenue' ? '#6366f1' : '#10b981'}
                strokeWidth={2.5}
                fill="url(#trendGrad)"
                dot={false}
                activeDot={{ r:5, strokeWidth:0 }}
                name={metric === 'revenue' ? 'Revenue' : 'Visits'}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   PIE — unique vs repeat revenue
───────────────────────────────────────────── */
const RevenuePie = ({ repeatRevenue, totalRevenue }) => {
  const newRevenue = Math.max(0, totalRevenue - repeatRevenue);
  const data = [
    { name:'Repeat Customers', value: repeatRevenue, fill:'#10b981' },
    { name:'New Customers',    value: newRevenue,     fill:'#6366f1' },
  ];
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0];
    return (
      <div className="chart-tooltip">
        <p className="ct-label">{p.name}</p>
        <p className="ct-row" style={{ color:p.payload.fill }}>
          <span>Revenue</span><span>₹{fmt(Math.round(p.value))}</span>
        </p>
        <p className="ct-row">
          <span>Share</span>
          <span>{totalRevenue ? ((p.value/totalRevenue)*100).toFixed(1) : 0}%</span>
        </p>
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={3} dataKey="value">
          {data.map((d, i) => <Cell key={i} fill={d.fill}/>)}
        </Pie>
        <Tooltip content={<CustomTooltip/>}/>
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:11 }}/>
      </PieChart>
    </ResponsiveContainer>
  );
};

/* ─────────────────────────────────────────────
   VISIT FREQUENCY COHORT HISTOGRAM
   Uses pre-bucketed data from backend:
   visitFrequencyCohorts: [{ label, customers }]
   Always max 8 bars — safe at any scale.
───────────────────────────────────────────── */
const FrequencyChart = ({ data = [] }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} margin={{ top:8, right:8, left:0, bottom:0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" vertical={false}/>
      <XAxis dataKey="label" tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false}/>
      <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false}/>
      <Tooltip
        formatter={(v) => [v, 'Customers']}
        contentStyle={{ borderRadius:10, border:'1px solid #e2e8f0', fontSize:12 }}
      />
      <Bar dataKey="customers" radius={[4,4,0,0]} name="Customers">
        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

/* ─────────────────────────────────────────────
   TREND SUMMARY CALLOUT STRIP
   trendSummary: { overallRevenueTrend,
                   totalPeriods,
                   peakPeriod, worstPeriod }
   aggregation: "daily" | "weekly" | "monthly"
───────────────────────────────────────────── */
const TrendSummaryStrip = ({ trendSummary, aggregation }) => {
  if (!trendSummary) return null;
  const { overallRevenueTrend, totalPeriods, peakPeriod, worstPeriod } = trendSummary;

  const labelMap = { daily:'days', weekly:'weeks', monthly:'months' };

  const formatPeriodLabel = (period) => {
    if (!period) return '—';
    // Monthly
    if (/^\d{4}-\d{2}$/.test(period)) {
      const [y, m] = period.split('-');
      const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(m)-1];
      return `${mon} ${y}`;
    }
    // Weekly
    if (/W\d{2}/.test(period)) return period;
    // Daily
    if (/^\d{4}-\d{2}-\d{2}$/.test(period)) {
      const d = new Date(period);
      return d.toLocaleDateString('en-IN', { day:'numeric', month:'short' });
    }
    return period;
  };

  const trendC = overallRevenueTrend === null ? 'flat' : clr(overallRevenueTrend);

  return (
    <div className="trend-summary-strip">
      <div className="tss-item">
        <span className="tss-icon">📅</span>
        <div>
          <p className="tss-label">Data Span</p>
          <p className="tss-val">{totalPeriods} {labelMap[aggregation]}</p>
        </div>
      </div>
      <div className="tss-item">
        <span className="tss-icon">{trendC === 'pos' ? '📈' : trendC === 'neg' ? '📉' : '➡️'}</span>
        <div>
          <p className="tss-label">Overall Revenue Trend</p>
          <p className={`tss-val tss-${trendC}`}>
            {overallRevenueTrend === null
              ? 'Not enough data'
              : `${sign(overallRevenueTrend)}${Math.abs(overallRevenueTrend).toFixed(1)}% (first half vs second half)`}
          </p>
        </div>
      </div>
      {peakPeriod && (
        <div className="tss-item">
          <span className="tss-icon">🏆</span>
          <div>
            <p className="tss-label">Peak Period</p>
            <p className="tss-val">{formatPeriodLabel(peakPeriod.period)} · ₹{fmt(Math.round(peakPeriod.revenue))}</p>
          </div>
        </div>
      )}
      {worstPeriod && (
        <div className="tss-item">
          <span className="tss-icon">⚠️</span>
          <div>
            <p className="tss-label">Lowest Period</p>
            <p className="tss-val">{formatPeriodLabel(worstPeriod.period)} · ₹{fmt(Math.round(worstPeriod.revenue))}</p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   GROWTH HEALTH SCORE GAUGE
───────────────────────────────────────────── */
const GrowthHealthGauge = ({ scores }) => {
  const health = scores?.growthHealthScore ?? 0;
  const band =
    health >= 70 ? { label:'Thriving',    color:'#10b981', bg:'#dcfce7' } :
    health >= 45 ? { label:'Stable',      color:'#f59e0b', bg:'#fef9c3' } :
    health >= 25 ? { label:'Developing',  color:'#6366f1', bg:'#ede9fe' } :
                   { label:'Early Stage', color:'#94a3b8', bg:'#f1f5f9' };

  const MAX_SCORE   = 100;
  const safeHealth  = Math.min(health, MAX_SCORE);
  const r           = 60;
  const cx          = 80;
  const cy          = 80;
  const startAngle  = 210;
  const totalArc    = 240;
  const filledArc   = (safeHealth / MAX_SCORE) * totalArc;

  const polarToXY = (angleDeg, radius) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };

  const describeArc = (startDeg, arcDeg) => {
    const start = polarToXY(startDeg, r);
    const end   = polarToXY(startDeg - arcDeg, r);
    const large = arcDeg > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y}`;
  };

  const subScores = [
    { label:'Acquisition',    value: scores?.acquisitionScore    ?? 0, color:'#6366f1', max:10 },
    { label:'Loyalty',        value: scores?.loyaltyScore        ?? 0, color:'#10b981', max:40 },
    { label:'Sustainability', value: scores?.sustainabilityScore ?? 0, color:'#f59e0b', max:30 },
  ];

  return (
    <div className="ghs-container">
      <div className="ghs-gauge-wrap">
        <svg viewBox="0 0 160 110" width="200" height="140">
          <path d={describeArc(startAngle, totalArc)} fill="none" stroke="#f1f5f9" strokeWidth="14" strokeLinecap="round"/>
          {safeHealth > 0 && (
            <path d={describeArc(startAngle, filledArc)} fill="none" stroke={band.color} strokeWidth="14" strokeLinecap="round"
              style={{ transition:'stroke-dasharray .9s ease, stroke .5s ease' }}/>
          )}
          <text x={cx} y={cy - 6}  textAnchor="middle" fontSize="22" fontWeight="800" fill="#1e293b">{health.toFixed(0)}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9"  fill="#94a3b8">/ 100 Score</text>
          <text x={cx} y={cy + 27} textAnchor="middle" fontSize="10" fontWeight="700" fill={band.color}>{band.label}</text>
        </svg>
      </div>
      <div className="ghs-sub-scores">
        {subScores.map(s => (
          <div key={s.label} className="ghs-sub-row">
            <span className="ghs-sub-label">{s.label}</span>
            <div className="ghs-sub-bar-track">
              <div className="ghs-sub-bar-fill" style={{
                width:      `${Math.min(100, (s.value / s.max) * 100)}%`,
                background: s.color,
              }}/>
            </div>
            <span className="ghs-sub-val" style={{ color:s.color }}>{s.value.toFixed(1)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   LIFECYCLE BANNER
───────────────────────────────────────────── */
const LIFECYCLE_STAGES = [
  { key:'Early Traction',        icon:'🌱', color:'#94a3b8', months:[0,6]    },
  { key:'Growth Discovery',      icon:'🚀', color:'#6366f1', months:[6,18]   },
  { key:'Behavior Intelligence', icon:'🧠', color:'#10b981', months:[18,36]  },
  { key:'Market Maturity',       icon:'🏆', color:'#f59e0b', months:[36,72]  },
  { key:'Legacy & Optimization', icon:'💎', color:'#ec4899', months:[72,999] },
];

const LifecycleBanner = ({ growthStage, confidence }) => {
  if (!growthStage) return null;
  const { phase, growthFocus, businessAgeMonths, analyticsAgeMonths } = growthStage;
  const stageInfo = LIFECYCLE_STAGES.find(s => s.key === phase) || LIFECYCLE_STAGES[0];
  const years     = Math.floor(businessAgeMonths / 12);
  const months    = businessAgeMonths % 12;
  const ageStr    = years > 0 ? `${years}y ${months}m` : `${months}m`;
  const totalLifecycleMonths = 120;
  const lifecyclePct = Math.min(100, (businessAgeMonths / totalLifecycleMonths) * 100);

  return (
    <div className="lifecycle-banner" style={{ '--lc-color': stageInfo.color }}>
      <div className="lcb-left">
        <div className="lcb-stage-icon">{stageInfo.icon}</div>
        <div className="lcb-info">
          <div className="lcb-phase-row">
            <span className="lcb-phase">{phase}</span>
            <span className="lcb-conf-badge" style={{
              background: confidence?.level === 'High' ? '#dcfce7' : '#fef9c3',
              color:       confidence?.level === 'High' ? '#166534' : '#92400e',
            }}>
              {confidence?.level ?? 'Medium'} Confidence · {confidence?.score ?? 0}%
            </span>
          </div>
          <p className="lcb-focus">{growthFocus}</p>
          <div className="lcb-ages">
            <span>🏠 Business: {ageStr} old</span>
            <span className="lcb-sep">·</span>
            <span>📊 Analytics: {analyticsAgeMonths}m of data</span>
          </div>
        </div>
      </div>
      <div className="lcb-timeline">
        <div className="lcb-tl-track">
          <div className="lcb-tl-fill" style={{ width:`${lifecyclePct}%`, background: stageInfo.color }}/>
          {LIFECYCLE_STAGES.slice(0, -1).map((s, i) => {
            const markPct = (s.months[1] / totalLifecycleMonths) * 100;
            return <div key={i} className="lcb-tl-mark" style={{ left:`${markPct}%` }}/>;
          })}
        </div>
        <div className="lcb-tl-labels">
          {LIFECYCLE_STAGES.map((s, i) => (
            <span key={i} className={`lcb-tl-label ${s.key === phase ? 'lcb-tl-active' : ''}`}
              style={s.key === phase ? { color: stageInfo.color } : {}}>
              {s.icon}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   INSIGHTS PANEL
───────────────────────────────────────────── */
const InsightsPanel = ({ insights = [], recommendations = [] }) => {
  const [tab, setTab] = useState('insights');
  const items   = tab === 'insights' ? insights : recommendations;
  const isEmpty = items.length === 0;

  return (
    <div className="insights-panel">
      <div className="ip-tabs">
        <button className={`ip-tab ${tab==='insights'?'ip-tab-on':''}`} onClick={() => setTab('insights')}>
          💡 Insights <span className="ip-count">{insights.length}</span>
        </button>
        <button className={`ip-tab ${tab==='reco'?'ip-tab-on':''}`} onClick={() => setTab('reco')}>
          🎯 Recommendations <span className="ip-count">{recommendations.length}</span>
        </button>
      </div>
      <div className="ip-body">
        {isEmpty ? (
          <p className="ip-empty">No {tab === 'insights' ? 'insights' : 'recommendations'} available.</p>
        ) : (
          <ul className="ip-list">
            {items.map((item, i) => (
              <li key={i} className={`ip-item ${tab === 'reco' ? 'ip-item-reco' : ''}`}>
                <span className="ip-bullet">{tab === 'insights' ? '●' : '→'}</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   TOP CUSTOMERS TABLE
───────────────────────────────────────────── */
const TopCustomersTable = ({ customers }) => {
  const maxSpend = Math.max(...customers.map(c => c.totalSpending || c.totalSpent || 0), 1);

  const getTier = (spend, max) => {
    const p = spend / max;
    if (p >= 0.8) return { label:'🏆 Top',  color:'#f59e0b', bg:'#fef9c3' };
    if (p >= 0.5) return { label:'🥈 High', color:'#6366f1', bg:'#ede9fe' };
    if (p >= 0.2) return { label:'🥉 Mid',  color:'#10b981', bg:'#dcfce7' };
    return              { label:'Regular', color:'#94a3b8', bg:'#f8fafc' };
  };

  const normalize = (c, i) => ({
    rank:       i + 1,
    id:         c.customerKey || c.customer_id || `cust-${i}`,
    name:       c.name || `Customer #${i + 1}`,
    phone:      c.phone || null,
    visits:     c.totalOrders     ?? c.visits     ?? 0,
    totalSpent: c.totalSpending   ?? c.totalSpent ?? 0,
    avgOrder:   c.avgOrderValue   ?? Math.round((c.totalSpending ?? 0) / Math.max(c.totalOrders ?? 1, 1)),
  });

  const rows = customers.map(normalize);

  return (
    <div className="tc-scroll">
      <table className="growth-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Customer</th>
            <th>Tier</th>
            <th>Visits</th>
            <th>Total Spent</th>
            <th>Avg / Visit</th>
            <th>Spend Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const tier = getTier(c.totalSpent, maxSpend);
            const barW = Math.round((c.totalSpent / maxSpend) * 100);
            return (
              <tr key={c.id}>
                <td className="gt-date">{c.rank}</td>
                <td>
                  <div className="tc-name-cell">
                    <div className="tc-avatar" style={{
                      background: COLORS[c.rank % COLORS.length] + '22',
                      color:      COLORS[c.rank % COLORS.length],
                    }}>
                      {c.name?.[0] || '#'}
                    </div>
                    <div>
                      <p className="tc-name">{c.name}</p>
                      {c.phone
                        ? <p className="tc-phone">{c.phone}</p>
                        : <p className="tc-phone tc-anon">Anonymous</p>}
                    </div>
                  </div>
                </td>
                <td>
                  <span className="cust-tier-badge" style={{ background:tier.bg, color:tier.color }}>
                    {tier.label}
                  </span>
                </td>
                <td className="gt-ord">{c.visits}</td>
                <td className="gt-rev">₹{fmt(Math.round(c.totalSpent))}</td>
                <td className="gt-aov">₹{fmt(Math.round(c.avgOrder))}</td>
                <td>
                  <div className="tc-bar-wrap">
                    <div className="tc-bar-track">
                      <div className="tc-bar-fill" style={{ width:`${barW}%`, background:tier.color }}/>
                    </div>
                    <span className="tc-bar-pct">{barW}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ─────────────────────────────────────────────
   AGGREGATION LABEL BADGE
───────────────────────────────────────────── */
const AggregationBadge = ({ aggregation }) => {
  const map = {
    daily:   { label:'Daily view',   color:'#6366f1', bg:'#ede9fe' },
    weekly:  { label:'Weekly view',  color:'#f59e0b', bg:'#fef9c3' },
    monthly: { label:'Monthly view', color:'#10b981', bg:'#dcfce7' },
  };
  const info = map[aggregation] || map.monthly;
  return (
    <span className="agg-badge" style={{ background:info.bg, color:info.color }}>
      📊 {info.label} · auto-scaled for readability
    </span>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const Growth = ({ cafeid }) => {
  const [view,    setView]    = useState('month');
  const [section, setSection] = useState('sales');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const [currData, setCurrData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [custData, setCustData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const p = periods[view]();
      if (section === 'customers') {
        const cd = await fetchCustomerAnalytics(cafeid, p.curr, p.prev);
        setCustData(cd);
      } else {
        const [curr, prev] = await Promise.all([
          fetchStats(cafeid, p.curr.from, p.curr.to),
          fetchStats(cafeid, p.prev.from, p.prev.to),
        ]);
        setCurrData(curr);
        setPrevData(prev);
      }
    } catch (e) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [cafeid, view, section]);

  useEffect(() => { load(); }, [load]);

  /* ── Derived — sales ── */
  const cSumm  = currData?.periodAnalytics?.summary;
  const pSumm  = prevData?.periodAnalytics?.summary;
  const cDays  = currData?.periodAnalytics?.dailyComparison || [];
  const pDays  = prevData?.periodAnalytics?.dailyComparison || [];
  const cItems = currData?.salesBreakdown?.topItems         || [];
  const pItems = prevData?.salesBreakdown?.topItems         || [];
  const p      = periods[view]();

  /* ── Derived — customers ── */
  const analytics   = custData?.analytics;
  const growthStage = analytics?.growthStage;
  const confidence  = analytics?.analyticsConfidence;
  const rawMetrics  = analytics?.rawMetrics;
  const wScores     = analytics?.weightedScores;
  const trendData   = rawMetrics?.trendData        || [];
  const trendSummary= analytics?.trendSummary;
  const aggregation = analytics?.aggregation        || 'monthly';
  const insights    = analytics?.insights           || [];
  const reco        = analytics?.recommendations    || [];

  const bestDay = cDays.reduce((b, d) => (d.revenue > (b?.revenue || 0) ? d : b), null);

  return (
    <div className="growth-shell">

      {/* ── Header ── */}
      <div className="growth-header">
        <div className="growth-title-block">
          <h1 className="growth-title">Growth Analytics</h1>
          <p className="growth-sub">Track performance trends across periods</p>
        </div>
        <div className="period-selector">
          {['week','month','year'].map(v => (
            <button key={v} className={`period-btn ${view===v?'period-on':''}`} onClick={() => setView(v)}>
              {v === 'week' ? 'Week' : v === 'month' ? 'Month' : 'Year'}
            </button>
          ))}
        </div>
      </div>

      {/* ── Period breadcrumb ── */}
      <div className="period-crumb">
        <span className="pc-curr">{p.curr.from} → {p.curr.to}</span>
        <span className="pc-sep">vs</span>
        <span className="pc-prev">{p.prev.from} → {p.prev.to}</span>
      </div>

      {/* ── Section tabs ── */}
      <div className="section-tabs">
        {[
          { key:'sales',     icon:'📈', label:'Sales Growth'       },
          { key:'dishes',    icon:'🍽️', label:'Dish Performance'   },
          { key:'customers', icon:'👥', label:'Customer Analytics' },
        ].map(s => (
          <button key={s.key} className={`st-btn ${section===s.key?'st-on':''}`} onClick={() => setSection(s.key)}>
            <span>{s.icon}</span> {s.label}
          </button>
        ))}
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="growth-error">
          <span>⚠️</span>
          <div>
            <p className="ge-title">Failed to load data</p>
            <p className="ge-msg">{error}</p>
          </div>
          <button className="ge-retry" onClick={load}>↻ Retry</button>
        </div>
      )}

      {/* ── Skeleton ── */}
      {loading && (
        <div className="growth-skel">
          <div className="skel-banner"/>
          <div className="skel-grid">{[1,2,3].map(i => <div key={i} className="skel-card"/>)}</div>
          <div className="skel-chart"/>
        </div>
      )}

      {/* ════ SALES SECTION ════ */}
      {!loading && section === 'sales' && currData && prevData && (
        <>
          <GrowthBanner curr={cSumm} prev={pSumm} period={p.label}/>
          <div className="growth-content">
            <div className="delta-grid">
              <DeltaCard label="Revenue"          curr={cSumm?.totalRevenue  || 0} prev={pSumm?.totalRevenue  || 0} prefix="₹" format="currency" delay={0}/>
              <DeltaCard label="Total Orders"     curr={cSumm?.totalOrders   || 0} prev={pSumm?.totalOrders   || 0} delay={80}/>
              <DeltaCard label="Avg Order Value"  curr={cSumm?.avgOrderValue || 0} prev={pSumm?.avgOrderValue || 0} prefix="₹" format="currency" delay={160}/>
            </div>

            {bestDay && (
              <div className="best-day-callout">
                <span className="bdc-icon">🏆</span>
                <div>
                  <p className="bdc-label">Best Day This Period</p>
                  <p className="bdc-val">{bestDay.date} — ₹{fmt(Math.round(bestDay.revenue))} · {bestDay.orders} orders</p>
                </div>
              </div>
            )}

            <div className="charts-grid">
              <div className="chart-card chart-wide">
                <div className="cc-hd">
                  <div>
                    <h3 className="cc-title">Revenue Trend</h3>
                    <p className="cc-sub">This period vs last period</p>
                  </div>
                  <div className="cc-legend">
                    <span className="cl-dot" style={{ background:'#6366f1' }}/><span className="cl-lbl">Current</span>
                    <span className="cl-dot" style={{ background:'#cbd5e1' }}/><span className="cl-lbl">Previous</span>
                  </div>
                </div>
                <div className="chart-area">
                  <ComparisonChart currData={cDays} prevData={pDays} dataKey="revenue" color="#6366f1" fmt={v=>`₹${fmt(Math.round(v))}`}/>
                </div>
              </div>
              <div className="chart-card">
                <div className="cc-hd"><div><h3 className="cc-title">Orders Volume</h3><p className="cc-sub">Daily order count comparison</p></div></div>
                <div className="chart-area chart-area-sm">
                  <ComparisonChart currData={cDays} prevData={pDays} dataKey="orders" color="#10b981"/>
                </div>
              </div>
              <div className="chart-card">
                <div className="cc-hd"><div><h3 className="cc-title">Avg Order Value</h3><p className="cc-sub">Daily AOV comparison</p></div></div>
                <div className="chart-area chart-area-sm">
                  <ComparisonChart currData={cDays} prevData={pDays} dataKey="aov" color="#f59e0b" fmt={v=>`₹${fmt(Math.round(v))}`}/>
                </div>
              </div>
              {view === 'year' && (
                <div className="chart-card chart-wide">
                  <div className="cc-hd"><div>
                    <h3 className="cc-title">Month-by-Month Revenue</h3>
                    <p className="cc-sub">This year vs last year · <span style={{ color:'#6366f1' }}>■</span> Above · <span style={{ color:'#f59e0b' }}>■</span> Below</p>
                  </div></div>
                  <div className="chart-area"><MonthlyBar data={cDays} prevData={pDays}/></div>
                </div>
              )}
            </div>

            {cDays.length > 0 && (
              <div className="table-card">
                <div className="tc-hd">
                  <h3 className="tc-title">Daily Breakdown</h3>
                  <p className="tc-sub">{cDays.length} data points</p>
                </div>
                <div className="tc-scroll">
                  <table className="growth-table">
                    <thead><tr><th>Date</th><th>Revenue</th><th>vs Prev</th><th>Orders</th><th>AOV</th></tr></thead>
                    <tbody>
                      {cDays.map((d, i) => {
                        const pd = pDays[i];
                        const g  = pd ? pct(d.revenue, pd.revenue) : null;
                        const c  = g === null ? 'flat' : clr(g);
                        return (
                          <tr key={d.date}>
                            <td className="gt-date">{d.date}</td>
                            <td className="gt-rev">₹{fmt(Math.round(d.revenue))}</td>
                            <td>{g !== null
                              ? <span className={`gt-delta gt-${c}`}>{c==='pos'?'↑':c==='neg'?'↓':'→'}{sign(g)}{Math.abs(g).toFixed(1)}%</span>
                              : <span className="gt-delta gt-flat">—</span>}
                            </td>
                            <td className="gt-ord">{d.orders}</td>
                            <td className="gt-aov">₹{fmt(Math.round(d.aov || (d.revenue / (d.orders || 1))))}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ════ DISHES SECTION ════ */}
      {!loading && section === 'dishes' && currData && prevData && (
        <div className="growth-content">
          {cItems.length > 0 && (
            <div className="dish-kpi-row">
              <div className="dish-kpi-card">
                <p className="dkc-label">Top Earner</p>
                <p className="dkc-val">{cItems[0]?.name}</p>
                <p className="dkc-sub">₹{fmt(cItems[0]?.revenue)}</p>
              </div>
              <div className="dish-kpi-card">
                <p className="dkc-label">Most Ordered</p>
                <p className="dkc-val">{[...cItems].sort((a,b)=>b.qty-a.qty)[0]?.name}</p>
                <p className="dkc-sub">{[...cItems].sort((a,b)=>b.qty-a.qty)[0]?.qty} orders</p>
              </div>
              <div className="dish-kpi-card dish-kpi-rising">
                <p className="dkc-label">Rising Star 🔥</p>
                {(() => {
                  const r = cItems
                    .map(ci => ({ ...ci, g: pct(ci.revenue, pItems.find(p => p.name === ci.name)?.revenue || 0) }))
                    .filter(ci => ci.g !== null && ci.g > 0)
                    .sort((a,b) => b.g - a.g)[0];
                  return r
                    ? <><p className="dkc-val">{r.name}</p><p className="dkc-sub">+{r.g.toFixed(1)}% vs prev</p></>
                    : <p className="dkc-val dkc-na">No new risers</p>;
                })()}
              </div>
              <div className="dish-kpi-card dish-kpi-warn">
                <p className="dkc-label">Needs Attention ⚠️</p>
                {(() => {
                  const d = cItems
                    .map(ci => ({ ...ci, g: pct(ci.revenue, pItems.find(p => p.name === ci.name)?.revenue || 0) }))
                    .filter(ci => ci.g !== null && ci.g < 0)
                    .sort((a,b) => a.g - b.g)[0];
                  return d
                    ? <><p className="dkc-val">{d.name}</p><p className="dkc-sub">{d.g.toFixed(1)}% vs prev</p></>
                    : <p className="dkc-val dkc-na">All items growing</p>;
                })()}
              </div>
            </div>
          )}

          {cItems.length > 0 && (
            <div className="chart-card chart-wide">
              <div className="cc-hd"><div>
                <h3 className="cc-title">Top Items Revenue Comparison</h3>
                <p className="cc-sub">Current period vs previous period</p>
              </div></div>
              <div className="chart-area">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={cItems.slice(0,10).map(ci => ({
                      name: ci.name.length > 16 ? ci.name.slice(0,14)+'…' : ci.name,
                      curr: ci.revenue,
                      prev: pItems.find(p => p.name === ci.name)?.revenue || 0,
                    }))}
                    margin={{ top:8, right:16, left:0, bottom:24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" vertical={false}/>
                    <XAxis dataKey="name" tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false} angle={-20} textAnchor="end"/>
                    <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
                    <Tooltip
                      formatter={(v, n) => [`₹${fmt(Math.round(v))}`, n==='curr'?'This Period':'Last Period']}
                      contentStyle={{ borderRadius:10, border:'1px solid #e2e8f0', fontSize:12 }}
                    />
                    <Bar dataKey="prev" fill="#e2e8f0" radius={[3,3,0,0]} name="prev"/>
                    <Bar dataKey="curr" radius={[3,3,0,0]} name="curr">
                      {cItems.slice(0,10).map((ci, i) => {
                        const pR = pItems.find(p => p.name === ci.name)?.revenue || 0;
                        return <Cell key={i} fill={ci.revenue >= pR ? '#6366f1' : '#f59e0b'}/>;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="chart-card chart-wide">
            <div className="cc-hd">
              <div>
                <h3 className="cc-title">Item-by-Item Growth</h3>
                <p className="cc-sub">
                  <span className="cc-legend-item pos">↑ Growing</span>
                  <span className="cc-legend-item neg">↓ Declining</span>
                  <span className="cc-legend-item flat">→ Stable / New</span>
                </p>
              </div>
            </div>
            <div className="dish-header-row">
              <span className="dhr-rank">#</span>
              <span className="dhr-name">Item</span>
              <span className="dhr-stats">Revenue · Growth</span>
              <span className="dhr-qty">Qty</span>
            </div>
            <div className="dish-list">
              {cItems.length === 0
                ? <div className="growth-empty">No item data for this period</div>
                : cItems.map((ci, i) => <DishGrowthRow key={ci.name} curr={ci} prev={pItems} rank={i+1}/>)}
            </div>
          </div>

          {(() => {
            const prevNames = new Set(pItems.map(p => p.name));
            const newItems  = cItems.filter(ci => !prevNames.has(ci.name));
            if (!newItems.length) return null;
            return (
              <div className="new-items-card">
                <div className="cc-hd">
                  <h3 className="cc-title">✨ New This Period</h3>
                  <p className="cc-sub">Items with no sales last cycle</p>
                </div>
                <div className="new-items-list">
                  {newItems.map(ni => (
                    <div key={ni.name} className="new-item-row">
                      <span className="ni-badge">NEW</span>
                      <span className="ni-name">{ni.name}</span>
                      <span className="ni-rev">₹{fmt(ni.revenue)}</span>
                      <span className="ni-qty">{ni.qty} sold</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ════ CUSTOMER ANALYTICS SECTION ════ */}
      {!loading && section === 'customers' && analytics && rawMetrics && (
        <div className="growth-content">

          {/* 1. Business Lifecycle Stage */}
          <LifecycleBanner growthStage={growthStage} confidence={confidence}/>

          {/* 2. Growth Health Score + Retention */}
          <div className="cust-hero-row">
            <div className="chart-card cust-gauge-card">
              <div className="cc-hd">
                <div>
                  <h3 className="cc-title">Growth Health Score</h3>
                  <p className="cc-sub">Composite of acquisition · loyalty · sustainability</p>
                </div>
              </div>
              <GrowthHealthGauge scores={wScores}/>
            </div>

            <div className="chart-card cust-gauge-card">
              <div className="cc-hd"><div>
                <h3 className="cc-title">Retention Rate</h3>
                <p className="cc-sub">Returning vs total customers</p>
              </div></div>
              <div className="gauge-center">
                <RetentionGauge value={rawMetrics.retentionRate || 0}/>
                <div className="gauge-legend">
                  <p className="gl-row"><span className="gl-dot" style={{ background:'#ef4444' }}/>{'<'}20% Weak</p>
                  <p className="gl-row"><span className="gl-dot" style={{ background:'#f59e0b' }}/>20–40% Decent</p>
                  <p className="gl-row"><span className="gl-dot" style={{ background:'#10b981' }}/>40%+ Excellent</p>
                </div>
              </div>
            </div>

            {/* Insight metric cards */}
            <div className="cust-ratio-grid">
              <InsightCard icon="🎯" title="Acquisition Rate"
                value={`${(rawMetrics.acquisitionRate || 0).toFixed(1)}%`}
                subtitle="New customers as % of total"
                color="#6366f1" delay={0}/>
              <InsightCard icon="🔄" title="Repeat-to-New Ratio"
                value={(rawMetrics.repeatToNewRatio || 0).toFixed(2)}
                subtitle={rawMetrics.repeatToNewRatio >= 1 ? 'Strong loyalty' : 'Dependent on new customers'}
                color={rawMetrics.repeatToNewRatio >= 1 ? '#10b981' : '#f59e0b'} delay={60}/>
              <InsightCard icon="💸" title="Revenue / Customer"
                value={`₹${fmt(Math.round(rawMetrics.revenuePerCustomer || 0))}`}
                subtitle="Average spend per customer"
                color="#6366f1" delay={120}/>
              <InsightCard icon="🔁" title="Visit Frequency"
                value={(rawMetrics.visitFrequency || 0).toFixed(2)}
                subtitle={rawMetrics.visitFrequency >= 2 ? 'Healthy regular base' : 'Mostly one-time visitors'}
                color={rawMetrics.visitFrequency >= 2 ? '#10b981' : '#94a3b8'} delay={180}/>
              <InsightCard icon="💎" title="Avg Customer Value"
                value={`₹${fmt(Math.round(rawMetrics.avgCustomerValue || 0))}`}
                subtitle="Revenue per visit × visit freq"
                color="#f59e0b" delay={240}/>
              <InsightCard icon="🏆" title="Loyalty Index"
                value={(rawMetrics.loyaltyIndex || 0).toFixed(2)}
                subtitle="Repeat × avg visits / unique"
                color="#6366f1" delay={300}/>
              <InsightCard icon="📊" title="Stability Score"
                value={`${(rawMetrics.customerStabilityScore || 0).toFixed(1)}/100`}
                subtitle="0.7 × retention + 0.3 × freq"
                color={rawMetrics.customerStabilityScore >= 60 ? '#10b981' : rawMetrics.customerStabilityScore >= 40 ? '#f59e0b' : '#ef4444'} delay={360}/>
              <InsightCard icon="💰" title="Repeat Revenue"
                value={`${(rawMetrics.repeatRevenuePercent || 0).toFixed(1)}%`}
                subtitle={`₹${fmt(Math.round(rawMetrics.repeatRevenue || 0))} from loyalists`}
                color="#10b981" delay={420}/>
            </div>
          </div>

          {/* 3. Volume delta cards */}
          <div className="delta-grid delta-grid-4">
            <DeltaCard icon="👤" label="Total Customers"  curr={rawMetrics.totalCustomers  || 0} prev={0} delay={0}/>
            <DeltaCard icon="✨" label="New Customers"    curr={rawMetrics.uniqueCustomers  || 0} prev={0} delay={60}/>
            <DeltaCard icon="🔄" label="Repeat Customers" curr={rawMetrics.repeatCustomers  || 0} prev={0} delay={120}/>
            <DeltaCard icon="🛒" label="Total Visits"     curr={rawMetrics.totalVisits      || 0} prev={0} delay={180}/>
          </div>

          {/* 4. Trend summary strip */}
          {/* <TrendSummaryStrip trendSummary={trendSummary} aggregation={aggregation}/> */}

          {/* 5. Unified trend chart (replaces dailyFootfall + dailyNewVsRepeat) */}
          {trendData.length > 0 && (
            <div className="chart-card chart-wide">
              <div className="cc-hd">
                <div>
                  <h3 className="cc-title">Customer Trend</h3>
                  <p className="cc-sub">
                    Revenue, visit volume and customer mix over time
                    <AggregationBadge aggregation={aggregation}/>
                  </p>
                </div>
              </div>
              <TrendChart data={trendData} aggregation={aggregation}/>
            </div>
          )}

          {/* 6. Revenue pie + Frequency cohort (always small / fixed size) */}
          <div className="charts-grid">
            <div className="chart-card">
              <div className="cc-hd"><div>
                <h3 className="cc-title">Revenue by Customer Type</h3>
                <p className="cc-sub">New vs repeat contribution</p>
              </div></div>
              <div className="chart-area chart-area-sm">
                <RevenuePie
                  repeatRevenue={rawMetrics.repeatRevenue || 0}
                  totalRevenue={rawMetrics.totalRevenue   || 0}
                />
              </div>
            </div>

            {rawMetrics.visitFrequencyCohorts?.length > 0 && (
              <div className="chart-card">
                <div className="cc-hd"><div>
                  <h3 className="cc-title">Visit Frequency Distribution</h3>
                  <p className="cc-sub">How often customers return (bucketed cohorts)</p>
                </div></div>
                <div className="chart-area chart-area-sm">
                  <FrequencyChart data={rawMetrics.visitFrequencyCohorts}/>
                </div>
              </div>
            )}
          </div>

          {/* 7. Top customers */}
          {rawMetrics.topCustomers?.length > 0 && (
            <div className="table-card">
              <div className="tc-hd">
                <h3 className="tc-title">Top Customers by Revenue</h3>
                <p className="tc-sub">Top {rawMetrics.topCustomers.length} spenders · tiered by spend share</p>
              </div>
              <TopCustomersTable customers={rawMetrics.topCustomers}/>
            </div>
          )}

          {/* 8. Insights & Recommendations */}
          <InsightsPanel insights={insights} recommendations={reco}/>

          {/* 9. Interpretation guide */}
          <div className="interpret-card">
            <h3 className="ic2-title">📖 How to Read These Metrics</h3>
            <div className="interpret-grid">
              {[
                { metric:'Retention Rate',      good:'≥ 40%', ok:'20–40%', bad:'< 20%', tip:'High repeat % = loyal base. Focus on food quality & consistency.' },
                { metric:'Repeat-to-New Ratio', good:'≥ 1.0', ok:'0.5–1.0',bad:'< 0.5', tip:'< 1 means growth depends on marketing; push loyalty programs.' },
                { metric:'Visit Frequency',     good:'≥ 3×',  ok:'1.5–3×', bad:'< 1.5', tip:'Low freq may indicate long gaps; consider reminder/offers strategy.' },
                { metric:'Stability Score',     good:'≥ 60',  ok:'40–60',  bad:'< 40',  tip:'Composite health score. Improve by boosting retention & visit freq.' },
                { metric:'Growth Health Score', good:'≥ 70',  ok:'45–70',  bad:'< 45',  tip:'Combines acquisition, loyalty & sustainability. Aim to grow all three.' },
                { metric:'Loyalty Index',       good:'≥ 10',  ok:'5–10',   bad:'< 5',   tip:'Reflects depth of repeat engagement. Reward your top regulars.' },
              ].map(r => (
                <div key={r.metric} className="interpret-row">
                  <p className="ir-metric">{r.metric}</p>
                  <div className="ir-bands">
                    <span className="ir-good">✓ {r.good}</span>
                    <span className="ir-ok">◐ {r.ok}</span>
                    <span className="ir-bad">✗ {r.bad}</span>
                  </div>
                  <p className="ir-tip">{r.tip}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* ── No data states ── */}
      {!loading && !error && section !== 'customers' && (!currData || !prevData) && (
        <div className="growth-empty-state">
          <div className="ges-icon">📊</div>
          <h3>No data yet</h3>
          <p>Growth analytics will appear once you have order history.</p>
        </div>
      )}
      {!loading && !error && section === 'customers' && !analytics && (
        <div className="growth-empty-state">
          <div className="ges-icon">👥</div>
          <h3>No customer data yet</h3>
          <p>Customer analytics will appear once you have completed orders.</p>
        </div>
      )}

    </div>
  );
};

export default Growth;